import React, { useCallback, useEffect, useState } from 'react';
import classes from './style.module.scss';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import LineTypeIcon from '~/workorder/Detail/LineTypeIcon';
import { useStoreActions, useStoreState } from '~/store';
import useAccessControl from '~/app/hooks/useAccessControl';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { PartLinkTypes, PartTypes } from '~/app/data/statuses/issue';
import { isPartsEditable } from '~/parts/helper';
import { formatPrice } from '~/app/helpers/format';
import AddPartDialog from '~/parts/Add';
import ManualPartForm from '~/parts/Add/ManualPartForm';
import QuantityEditForm from '~/parts/QuantityEditForm';
import PartRowMenu from '~/parts/PartRowMenu';
import { Part, SavePartResponse } from '~/types';
import { IssueOrServiceType } from '~/app/data/statuses/chat';

export type PartsComponent = React.FC;

const Parts: PartsComponent = () => {
  const { t } = useTranslation('workorder');
  const { t: tParts } = useTranslation('parts');
  const { checkPermission } = useAccessControl();

  const [expanded, setExpanded] = useState(false);
  const [isOpenAddPartForm, setIsOpenAddPartForm] = useState(false);
  const [partForEdit, setPartForEdit] = useState<Part>();
  const [isOpenManualPartForm, setIsOpenManualPartForm] = useState(false);
  const [isOpenQuantityEditForm, setIsOpenQuantityEditForm] = useState(false);

  const parts = useStoreState(state => state.workOrder.data.parts ?? []);
  const id = useStoreState(state => state.workOrder.data.id);
  const status = useStoreState(state => state.workOrder.data.status);
  const deptPrefix = useStoreState(state => state.workOrder.data.departmentPrefix);
  const netWorkStatus = useStoreState(state => state.app.offline.status);

  const fetchPartsAction = useStoreActions(actions => actions.workOrder.fetchParts);
  const setTempPartsAction = useStoreActions(actions => actions.workOrder.setTempParts);
  const savePartAction = useStoreActions(actions => actions.workOrder.savePart);

  const addPartPermission = deptPrefix
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.addParts(deptPrefix))
    : false;

  const canEdit =
    !netWorkStatus
    && isPartsEditable(PartLinkTypes.WORKORDER, status)
    && addPartPermission;

  useEffect(() => {
    if (!netWorkStatus && id) {
      fetchPartsAction(String(id));
    }
  }, [id, fetchPartsAction, netWorkStatus]);

  const toggleAddDialog = useCallback((open: boolean) => (): void => {
    setTempPartsAction(parts);
    setIsOpenAddPartForm(open);
  }, [parts, setTempPartsAction]);

  const persistParts = useCallback((nextParts: Part[], partId: string) => {
    setTempPartsAction(nextParts);
    return savePartAction({
      id: String(id),
      parts: nextParts.filter(m => m.id === partId),
    }).then((res: SavePartResponse) => {
      if (res.success) {
        fetchPartsAction(String(id));
      }
      return res;
    });
  }, [id, setTempPartsAction, savePartAction, fetchPartsAction]);

  const onRemove = useCallback((partToRemove: Part) => {
    const nextParts = parts.map(part =>
      part.id === partToRemove.id
        ? { ...part, deleted: true, added: undefined }
        : { ...part, added: undefined }
    );
    persistParts(nextParts, partToRemove.id);
  }, [parts, persistParts]);

  const onEdit = useCallback((part: Part) => {
    setTempPartsAction(parts.map(m => ({ ...m, added: undefined })));
    setPartForEdit(part);
    if (part.type === PartTypes.MANUAL) {
      setIsOpenManualPartForm(true);
    } else {
      setIsOpenQuantityEditForm(true);
    }
  }, [parts, setTempPartsAction]);

  const closeManualForm = useCallback(() => {
    setIsOpenManualPartForm(false);
    setPartForEdit(undefined);
  }, []);

  const closeQuantityForm = useCallback(() => {
    setIsOpenQuantityEditForm(false);
    setPartForEdit(undefined);
  }, []);

  const onQuantitySave = useCallback((part: Part, quantity: number) => {
    const nextParts = parts.map(m =>
      m.id === part.id
        ? { ...m, quantity, added: undefined }
        : { ...m, added: undefined }
    );
    persistParts(nextParts, part.id);
  }, [parts, persistParts]);

  return (
    <>
      <div className={classes.card}>
        <div className={classes['header']} onClick={() => setExpanded(prev => !prev)}>
          <h2 className={classes['header-title']}>
            <Icon name="build" size={20} className={classes['section-header-icon']} />
            {tParts('parts')}
          </h2>
          {canEdit && (
            <button
              type="button"
              className={classes['add-action']}
              onClick={(e) => {
                e.stopPropagation();
                toggleAddDialog(true)();
              }}
            >
              + {tParts('add')}
            </button>
          )}
          <Icon name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} className={classes['chevron']} />
        </div>
        {expanded && (
          parts.length === 0 ? (
            <p className={classes.empty}>{t('detail.noParts')}</p>
          ) : (
            <div className={classes.list}>
              {parts.map(part => (
                <div key={part.id} className={classes['list-row']}>
                  {part.fromType !== undefined && (
                    <LineTypeIcon
                      type={part.fromType as IssueOrServiceType}
                      issueId={part.issueId ?? 0}
                    />
                  )}
                  <div className={classes['row-content']}>
                    <div className={classes['row-main']}>
                      <p className={classes['row-name']}>{part.article_name}</p>
                    </div>
                    <p className={classes['row-meta']}>
                      {tParts('partNo')}: {part.article_no}
                      {' · '}
                      {formatPrice(Number(part.price_per_unit) || 0)}
                      {' · '}
                      {part.quantity} {tParts('item')}
                    </p>
                  </div>
                  <div className={classes['row-actions']}>
                    {canEdit && (
                      <PartRowMenu
                        part={part}
                        onEdit={onEdit}
                        onRemove={onRemove}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
      {canEdit && (
        <>
          <AddPartDialog
            open={isOpenAddPartForm}
            onClose={toggleAddDialog(false)}
            type={PartLinkTypes.WORKORDER}
          />
          <ManualPartForm
            open={isOpenManualPartForm}
            onClose={closeManualForm}
            part={partForEdit}
            type={PartLinkTypes.WORKORDER}
            persistOnSave
          />
          <QuantityEditForm
            open={isOpenQuantityEditForm}
            onClose={closeQuantityForm}
            part={partForEdit}
            onSave={onQuantitySave}
          />
        </>
      )}
    </>
  );
};

export default Parts;
